import { styled } from '@mui/material/styles';
import { Box, Typography, Button } from '@mui/material';

// ============================================
// Card Outer
// ============================================
export const TicketCard = styled(Box, {
  shouldForwardProp: prop => prop !== 'isStopped' && prop !== 'subscription',
})(({ isStopped, subscription }) => ({
  width: '350px',
  borderRadius: '12px',
  // 구독 이용권은 브랜드 틸 테두리 + 연한 틸 틴트로 일반 이용권과 구분
  border: subscription && !isStopped ? '1px solid #4ab3bc' : '1px solid #d9d9d9',
  backgroundColor: isStopped ? '#D9D9D9' : subscription ? '#f3fafa' : '#fafbfc',
  padding: '16px',
  display: 'flex',
  flexDirection: 'column',
  gap: '16px',
}));

// 구독중 칩 — 제목 옆에 붙는 작은 필 배지 (정지 시 회색)
export const SubscriptionChip = styled(Typography, {
  shouldForwardProp: prop => prop !== 'isStopped',
})(({ isStopped }) => ({
  flexShrink: 0,
  fontSize: '11px',
  fontWeight: 600,
  lineHeight: 1,
  padding: '4px 8px',
  borderRadius: '10px',
  color: '#fff',
  backgroundColor: isStopped ? '#737373' : '#4ab3bc',
  letterSpacing: '-0.11px',
}));

export const ExpiredCardWrapper = styled(Box)({
  opacity: 0.6,
});

export const CardInfoSection = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  gap: '8px',
});

// ============================================
// Header Row — 카테고리/제목 + 종류 라벨
// ============================================
export const HeaderRow = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  width: '100%',
});

export const HeaderLeft = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  minWidth: 0,
});

export const ClassTypeLabel = styled(Typography, {
  shouldForwardProp: prop => prop !== 'isStopped' && prop !== 'clickable',
})(({ isStopped, clickable }) => ({
  flexShrink: 0,
  fontSize: '12px',
  color: isStopped ? '#737373' : '#737373',
  letterSpacing: '-0.12px',
  ...(clickable && {
    cursor: 'pointer',
    color: '#0F7F85',
    '&:hover': {
      textDecoration: 'underline',
    },
  }),
}));

export const TicketTitle = styled(Typography, {
  shouldForwardProp: prop => prop !== 'isStopped',
})(({ isStopped }) => ({
  fontSize: '14px',
  fontWeight: 600,
  color: isStopped ? '#262626' : '#262626',
  letterSpacing: '-0.14px',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
}));

export const TypeLabel = styled(Typography, {
  shouldForwardProp: prop => prop !== 'isStopped',
})(({ isStopped }) => ({
  flexShrink: 0,
  fontSize: '14px',
  fontWeight: 600,
  color: isStopped ? '#262626' : '#4ab3bc',
  letterSpacing: '-0.14px',
}));

// ============================================
// Info Row — Row 2(잔여횟수) / Row 3(유효기간) 공용
// ============================================
export const InfoRow = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  width: '100%',
});

export const InfoLeft = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
});

export const InfoLabel = styled(Typography, {
  shouldForwardProp: prop => prop !== 'isStopped',
})(({ isStopped }) => ({
  fontSize: '14px',
  color: isStopped ? '#737373' : '#737373',
  letterSpacing: '-0.14px',
}));

export const InfoValue = styled(Typography, {
  shouldForwardProp: prop => prop !== 'isStopped',
})(({ isStopped }) => ({
  fontSize: '14px',
  fontWeight: 500,
  color: isStopped ? '#262626' : '#262626',
  letterSpacing: '-0.14px',
}));

// ============================================
// Count display — "25회 / 25회" (좌측, 분자/분모 분리 색)
// ============================================
export const CountGroup = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '2px',
  fontSize: '16px',
  letterSpacing: '-0.16px',
});

export const CountCurrent = styled('span', {
  shouldForwardProp: prop => prop !== 'isStopped',
})(({ isStopped }) => ({
  color: isStopped ? '#262626' : '#4ab3bc',
  fontWeight: 600,
}));

export const CountTotal = styled('span', {
  shouldForwardProp: prop => prop !== 'isStopped',
})(({ isStopped }) => ({
  color: isStopped ? '#262626' : '#737373',
  fontWeight: 500,
}));

// ============================================
// Right-side highlight — "25회 남음" / "16일 남음"
// urgent=true이면 빨강 (#EE735A) — 만료 임박(≤10일).
// ============================================
export const HighlightText = styled(Typography, {
  shouldForwardProp: prop => prop !== 'urgent' && prop !== 'isStopped',
})(({ urgent, isStopped }) => ({
  fontSize: '16px',
  fontWeight: 600,
  color: isStopped ? '#262626' : urgent ? '#ee735a' : '#4ab3bc',
  letterSpacing: '-0.16px',
}));

export const ExpiredLabel = styled(Typography)({
  fontSize: '14px',
  fontWeight: 500,
  color: '#737373',
  letterSpacing: '-0.14px',
});

export const UpcomingLabel = styled(Typography)({
  fontSize: '14px',
  fontWeight: 600,
  color: '#55C982',
  letterSpacing: '-0.14px',
});

// ============================================
// Card Buttons (기존 2x2 layout 유지)
// ============================================
export const CardButtonAreaColumn = styled(Box, {
  shouldForwardProp: prop => prop !== 'isStopped',
})(({ isStopped }) => ({
  backgroundColor: isStopped ? '#d9d9d9' : '#fff',
  display: 'flex',
  flexDirection: 'column',
  borderRadius: 0,
}));

export const CardButtonRow = styled(Box)({
  display: 'flex',
  width: '100%',
});

export const CardButton = styled(Button, {
  shouldForwardProp: prop =>
    prop !== 'isStopped' && prop !== 'noBorderRight' && prop !== 'noBorderLeft' && prop !== 'noBorderTop',
})(({ isStopped, noBorderRight, noBorderLeft, noBorderTop }) => ({
  flex: 1,
  border: isStopped ? '1px solid #fff' : '1px solid #d9d9d9',
  borderRadius: 0,
  padding: '8px 12px',
  fontSize: '12px',
  fontWeight: 500,
  color: isStopped ? '#262626' : '#262626',
  textTransform: 'none',
  letterSpacing: '-0.12px',
  minHeight: 'auto',
  ...(noBorderRight && { borderRight: 'none' }),
  ...(noBorderLeft && { borderLeft: 'none' }),
  ...(noBorderTop && { borderTop: 'none' }),
  '&:hover': {
    backgroundColor: isStopped ? 'transparent' : '#f5f5f5',
  },
  '&:disabled': {
    color: '#bdbdbd',
  },
}));

export const EmptyButtonPlaceholder = styled(Box)({
  flex: 1,
  border: '1px solid #d9d9d9',
  borderLeft: 'none',
});
